
import { memo, useEffect, useState, ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

interface RouteTransitionProps {
  children?: ReactNode;
}

export const RouteTransition = memo(({ children }: RouteTransitionProps) => {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  const [showOldContent, setShowOldContent] = useState(false);
  
  useEffect(() => {
    if (location.pathname !== prevPathname) {
      setIsTransitioning(true);
      setShowOldContent(true);
      
      // Short timeout to trigger fade out
      const timeout1 = setTimeout(() => {
        setShowOldContent(false);
      }, 50);
      
      // Longer timeout to complete transition
      const timeout2 = setTimeout(() => {
        setPrevPathname(location.pathname);
        setIsTransitioning(false);
      }, 300);
      
      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
      };
    }
  }, [location.pathname, prevPathname]);
  
  return (
    <div className="relative flex-1 w-full transition-opacity duration-300">
      {/* Fade out old content */}
      {showOldContent && isTransitioning && (
        <div 
          className={cn(
            "absolute inset-0 z-10 transition-opacity duration-300",
            "opacity-0"
          )}
        />
      )}
      
      {/* Current content */}
      <div 
        className={cn(
          "w-full h-full transition-opacity duration-300",
          isTransitioning ? "opacity-0" : "opacity-100"
        )}
      >
        {children || <Outlet />}
      </div>
    </div>
  );
});

RouteTransition.displayName = 'RouteTransition';
